'use client'

import cx from 'classnames'
import { Badge } from 'components/Badge'
import { Type } from 'components/Type'
import { generateID } from 'components/_utils/idUtils'
import { A } from 'components/basic/A'
import { Img } from 'components/basic/Img'
import { ACCESSORIES_FILTER } from 'constants/accessories'
import { IAccessoryCard } from 'constants/types'
import { PriceManager } from 'prices'
import { FC } from 'react'
import { useStore } from 'stores/StoreProvider'
import { useCurrency } from 'stores/prices'
import { useGetAccessoriesProducts, useGetMemberAccessoriesProducts } from 'stores/product'
import styles from './AccessoryCards.module.scss'
import { AccessoriesPageProps, AccessoryCardProps, AccessoryCardsProps } from './AccessoryCards.types'

export const AccessoryCards: FC<AccessoryCardsProps> = (props) => {
	const { eyebrow, header, cards, highlightedProduct, highlightCards } = props
	return (
		<section className={styles.container}>
			<div className={cx(styles.header_container, { [styles.header_grid]: !!highlightedProduct })}>
				{header && (
					<header>
						<Type.Eyebrow as={'h2'}>{eyebrow}</Type.Eyebrow>
						<Type.Headline3
							as={'p'}
							className={styles.headline}
						>
							{header}
						</Type.Headline3>
					</header>
				)}

				{highlightedProduct && (
					<div className={styles.highlighted_product}>
						<AccessoryCard
							{...highlightedProduct}
							onClick={() => props.onClick?.(highlightedProduct)}
						/>
					</div>
				)}
			</div>

			<ul className={styles.card_list}>
				{cards?.map((card) => (
					<li
						key={card.title}
						className={highlightCards ? styles.highlight_card : ''}
					>
						<AccessoryCard
							{...card}
							onClick={() => props.onClick?.(card)}
						/>
					</li>
				))}
			</ul>
		</section>
	)
}

export const AccessoryCard: FC<AccessoryCardProps> = (props) => {
	const { href, image, title, priceString, priceSuffix, strikePriceString, saleTag } = props

	return (
		<A
			href={href}
			id={`${generateID(title)}-accessory-card`}
			className={styles.accessory_card}
			onClick={props.onClick}
		>
			<span className={styles.image_container}>
				<Img
					src={image.src}
					alt={image.alt}
					className={`${styles.image} ${title.includes('Elixir') ? styles.small : ''}`}
				/>
				<Badge.Orange
					className={styles.sale_tag}
					type={'discount'}
				>
					{saleTag}
				</Badge.Orange>
			</span>
			<span className={styles.pricing_and_title}>
				<Type.Headline6 as={'span'}>{title}</Type.Headline6>
				<span className={styles.pricing}>
					{strikePriceString !== priceString && (
						<Type.Body2
							as={'del'}
							className={styles.strike_price}
						>
							{strikePriceString}
						</Type.Body2>
					)}

					<Type.Body2
						as={'span'}
						className={styles.price}
					>
						{priceString}
						{priceSuffix}
					</Type.Body2>
				</span>
			</span>
		</A>
	)
}

export const BaseAccessoriesPage: FC<AccessoriesPageProps> = (props) => {
	return (
		<div className={styles.page_container}>
			{props.sections.map((section) => (
				<AccessoryCards
					{...section}
					key={section.eyebrow + section.header}
					onClick={props.onClick}
				/>
			))}
		</div>
	)
}

export const AccessoriesPage = () => {
	const data = useGetAccessoriesProducts('accessories') //defined in config/url_promos
	const prices = useStore((state) => state.prices.allPrices)
	const currency = useCurrency()

	const setSelectedSelections = useStore((state) => state.shop.setSelectedSelections)
	const setPodShopData = useStore((state) => state.shop.setPodShopData)

	data.forEach((section) => {
		section.items.forEach((product) => {
			if (Object.keys(prices).length) {
				product.prices = PriceManager.formatPrices(prices[product.variantId].prices, currency)
			} else {
				product.prices = PriceManager.formatPrices(PriceManager.emptyPrice, currency)
			}
		})
	})

	const topSection = data.filter((section) => section.name === ACCESSORIES_FILTER.NewPodTechnology)[0].items
	const middleSection = data
		.filter((section) => section.name !== ACCESSORIES_FILTER.NewPodTechnology && section.name !== ACCESSORIES_FILTER.TheBase && section.name !== ACCESSORIES_FILTER.BoostYourRecovery)
		.map((s) => s.items)
		.flat()

	const bottomSection = data.find((section) => section.name === ACCESSORIES_FILTER.BoostYourRecovery)?.items

	const sections: AccessoryCardsProps[] = [
		{
			eyebrow: 'Accessories',
			highlightCards: true,
			header: 'Everything you need to optimize your sleep fitness',
			cards: legacyCardsToModernCards(topSection),
		},
	]

	if (middleSection.length > 0 || (bottomSection && bottomSection.length > 0)) {
		sections.push({
			eyebrow: '',
			header: '',
			cards: [legacyCardsToModernCards(middleSection, true), bottomSection && bottomSection.length > 0 ? legacyCardsToModernCards(bottomSection, true) : []].flat(),
		})
	}

	const handleClick = (card: AccessoryCardProps) => {
		if (card.href === '/product/pod4') {
			setSelectedSelections({ 'pod-model': 'pod_4' })
			setPodShopData()
		}
	}

	return (
		<BaseAccessoriesPage
			sections={sections}
			onClick={handleClick}
		/>
	)
}

export const MemberAccessoriesPage = () => {
	const currency = useCurrency()
	const showBase = currency === 'USD'

	const data = useGetMemberAccessoriesProducts('member-accessories', 'member-accessories=1') //defined in config/url_promos
	const prices = useStore((state) => state.prices.allPrices)

	const setSelectedSelections = useStore((state) => state.shop.setSelectedSelections)
	const setPodShopData = useStore((state) => state.shop.setPodShopData)

	data.forEach((section) => {
		section.items.forEach((product) => {
			if (Object.keys(prices).length) {
				product.prices = PriceManager.formatPrices(prices[product.variantId].prices, currency)
			} else {
				product.prices = PriceManager.formatPrices(PriceManager.emptyPrice, currency)
			}
		})
	})

	const topSection = data.filter((section) => section.name === ACCESSORIES_FILTER.NewPodTechnology)[0].items
	const bottomSection = data
		.filter((section) => section.name !== ACCESSORIES_FILTER.NewPodTechnology && section.name !== ACCESSORIES_FILTER.TheBase)
		.map((s) => s.items)
		.flat()

	const baseProducts = data.filter((section) => section.name === ACCESSORIES_FILTER.TheBase)[0].items
	baseProducts.forEach((product) => {
		product.productTitle = product.productTitle.replace('The Base', 'The Base (compatible only with Pod 4)')
	})

	const sections: AccessoryCardsProps[] = [
		{
			eyebrow: 'Member Deals',
			header: 'Shop the latest Pod generation',
			cards: legacyCardsToModernCards(topSection),
		},
	]

	if (bottomSection.length > 0) {
		sections.push({
			eyebrow: 'Accessories',
			header: 'Complete your sleep ecosystem',
			cards: legacyCardsToModernCards(bottomSection, true),
			highlightedProduct: showBase ? legacyCardsToModernCards(baseProducts)[0] : undefined,
		})
	}

	const handleClick = (card: AccessoryCardProps) => {
		if (card.href === '/product/pod4') {
			setSelectedSelections({ 'pod-model': 'pod_4' })
			setPodShopData()
		}
	}

	return (
		<BaseAccessoriesPage
			sections={sections}
			onClick={handleClick}
		/>
	)
}

const forceDollars = (accessory: IAccessoryCard) => {
	return accessory.handle.includes('bundle') || accessory.handle.includes('mattress')
}

const legacyCardsToModernCards = (accessoriesList: IAccessoryCard[], percentageDiscount = false): AccessoryCardProps[] => {
	const calculateSaleTag = (accessory: IAccessoryCard) => {
		if (accessory.badge === '') return null

		const priceDifference = accessory.prices.comparePrice - accessory.prices.price

		if (priceDifference > 0) {
			if (!percentageDiscount || forceDollars(accessory)) {
				return `Save ${PriceManager.formatPriceToCurrencyNoDecimal(priceDifference / 100, accessory.prices.currency)}`
			}
			return `Save ${Math.round((priceDifference / accessory.prices.comparePrice) * 100).toString()}%`
		}

		return null
	}

	return accessoriesList.map((accessory) => {
		return {
			href: accessory.buttonTo,
			image: {
				src: accessory.productImage,
				alt: accessory.productTitle,
			},
			title: accessory.productTitle,
			priceString: accessory.prices.priceString,
			strikePriceString: accessory.prices.comparePriceString,
			priceSuffix: accessory.priceSuffix,
			saleTag: calculateSaleTag(accessory),
		}
	})
}
